import { useEffect, useMemo, useReducer } from "react";
import { FormModalState } from "../../../../Modal/modals/FormModal";
import Modal, { ModalRoot } from "../../../../Modal/Modal";
import EditIcon from "../../../../icons/EditIcon";
import AlertRoot, { Alert } from "../../../../Alert/Alert";
import Button from "@/components/bricks/button/Button";
import { useMutation, useQuery } from "@tanstack/react-query";
import { del, get, patch } from "../../../../../api/util";
import { Form, Formik, FormikProps } from "formik";
import { TextInputFormik } from "../../../../formcontrols/InputItem";
import { FormikDatePicker } from "../../../../formcontrols/DatePicker";
import { StickyFooter } from "../../../../stickyFooter/modalStickyFooter";
import { useConfirm } from "../../../../confirm/useConfirm";
import DeleteIcon from "../../../../icons/DeleteIcon";
import Spinner from "../../../../spinner/Spinner";
import { AlertMessage } from "../../../../AlertMessage";
import { getPatchOperations } from "../../../../../util/form";
import { FormikKodeSelect } from "../../../../formcontrols/KodeSelect";
import * as Yup from "yup";
import { differenceInDays } from "date-fns";

export default function EfteruddannelseEditFormModal(props: FormModalProps) {
  const {
    efteruddannelseId,
    efteruddannelseIndberetningId,
    personId,
    invalidateData,
    kanSletteIndberetning,
  } = props;
  const [formModalState, setFormModalState] = useReducer(
    (data: FormModalState, partialData: Partial<FormModalState>) => ({
      ...data,
      ...partialData,
    }),
    {
      open: false,
      shouldWarnBeforeClosing: false,
      warningOpen: false,
      isSaving: false,
      isInvalidating: false,
    },
  );
  const setShouldWarnBeforeClosing = (shouldWarnBeforeClosing: boolean) => {
    setFormModalState({ shouldWarnBeforeClosing });
  };
  const {
    data: personEfteruddannelseData,
    isFetching: personEfteruddannelseIsFetching,
  } = useQuery<PersonEfteruddannelseDTO>({
    queryKey: ["personEfteruddannelse", personId, efteruddannelseId],
    queryFn: async function () {
      return get({
        query: `person/${personId}/efteruddannelser/${efteruddannelseId}`,
      });
    },
    enabled: formModalState.open,
  });

  const {
    data: personEfteruddannelseIndberetningData,
    isFetching: personEfteruddannelseIndberetningIsFetching,
  } = useQuery<PersonEfteruddannelseIndberetningDTO>({
    queryKey: [
      "personEfteruddannelseIndberetning",
      personId,
      efteruddannelseId,
      efteruddannelseIndberetningId,
    ],
    queryFn: async function () {
      return get({
        query: `person/${personId}/efteruddannelser/${efteruddannelseId}/indberetninger/${efteruddannelseIndberetningId}`,
      });
    },
    enabled:
      formModalState.open && Boolean(efteruddannelseIndberetningId) === true,
  });

  // Delete indberetninger on periode
  const {
    mutate: deleteIndberetninger,
    error: deleteIndberetningerError,
    reset: deleteIndberetningerReset,
    isPending: deleteIndberetningerIsLoading,
  } = useMutation<unknown, PatchOperationError>({
    onSuccess: () => {
      invalidateData();
      setFormModalState({ open: false });
    },
    onError: () => {
      setTimeout(() => {
        deleteIndberetningerReset();
      }, 10000);
    },
    mutationFn: () => {
      return del({
        query: `person/${personId}/efteruddannelser/${efteruddannelseId}/indberetninger`,
      });
    },
  });

  // Patch periode
  const {
    mutate: patchPeriode,
    error: patchPeriodeError,
    isPending: patchPeriodeIsLoading,
    reset: patchPeriodeReset,
  } = useMutation<any, PatchOperationError, Partial<FormDTO>>({
    onSuccess: () => {
      invalidateData();
      setFormModalState({ open: false });
    },
    onError: () => {
      setTimeout(() => {
        patchPeriodeReset();
      }, 10000);
    },
    mutationFn: async (body: any) => {
      const values = body as Partial<FormDTO>;
      const {
        bemaerkning,
        efteruddannelseIndberetningStatus,
        lektionerGennemfoert,
        registreringsDato,
      } = values;

      if (bemaerkning !== initialValues?.bemaerkning) {
        await patch({
          body: JSON.stringify(
            getPatchOperations({ bemaerkning: bemaerkning }),
          ),
          query: `person/${personId}/efteruddannelser/${efteruddannelseId}`,
        });
      }

      const changed: Partial<FormDTO> = {};
      if (
        efteruddannelseIndberetningStatus !==
        initialValues?.efteruddannelseIndberetningStatus
      ) {
        changed.efteruddannelseIndberetningStatus =
          efteruddannelseIndberetningStatus;
      }
      if (lektionerGennemfoert !== initialValues?.lektionerGennemfoert) {
        changed.lektionerGennemfoert = lektionerGennemfoert;
      }

      if (registreringsDato !== initialValues?.registreringsDato) {
        changed.registreringsDato = registreringsDato;
      }

      if (efteruddannelseIndberetningStatus === "Henlagt") {
        delete changed.lektionerGennemfoert;
        delete changed.registreringsDato;
      }

      if (Object.keys(changed).length > 0) {
        await patch({
          body: JSON.stringify(getPatchOperations(changed)),
          query: `person/${personId}/efteruddannelser/${efteruddannelseId}/indberetninger/${efteruddannelseIndberetningId}`,
        });
      }
    },
  });

  const initialValues = useMemo<FormDTO | null>(() => {
    if (!personEfteruddannelseData) return null;
    return {
      bemaerkning: personEfteruddannelseData?.bemaerkning ?? "",
      efteruddannelseIndberetningStatus:
        personEfteruddannelseIndberetningData?.efteruddannelseIndberetningStatus ??
        "",
      lektionerGennemfoert:
        personEfteruddannelseIndberetningData?.lektionerGennemfoert ?? 0,
      registreringsDato:
        personEfteruddannelseIndberetningData?.registreringsDato ?? "",
    };
  }, [personEfteruddannelseData, personEfteruddannelseIndberetningData]);

  const isLoading =
    personEfteruddannelseIsFetching ||
    personEfteruddannelseIndberetningIsFetching ||
    patchPeriodeIsLoading ||
    deleteIndberetningerIsLoading;
  const validationSchema = useMemo(
    () => getValidationSchema(initialValues, !efteruddannelseIndberetningId),
    [initialValues, efteruddannelseIndberetningId],
  );
  return (
    <ModalRoot
      controller={{
        open: formModalState.open,
        setOpen: (open) => {
          if (formModalState.isSaving) {
            // don't close while loading
          } else if (formModalState.shouldWarnBeforeClosing) {
            setFormModalState({ warningOpen: true });
          } else {
            setFormModalState({ open });
          }
        },
      }}
    >
      <Modal
        title={
          <>
            <EditIcon height={16} /> Redigér efteruddannelse
          </>
        }
        description=""
      >
        <div className="c-modal-container">
          {initialValues && (
            <Formik
              enableReinitialize
              initialValues={initialValues}
              onSubmit={(values) => patchPeriode(values)}
              validationSchema={validationSchema}
            >
              {(formik) => {
                return (
                  <EfteruddannelseEditForm
                    formModalState={formModalState}
                    formik={formik}
                    initialValues={initialValues}
                    efteruddannelseIndberetningId={
                      efteruddannelseIndberetningId
                    }
                    deleteIndberetninger={deleteIndberetninger}
                    deleteIndberetningerError={deleteIndberetningerError}
                    isLoading={isLoading}
                    patchPeriodeError={patchPeriodeError}
                    setShouldWarnBeforeClosing={setShouldWarnBeforeClosing}
                    kanSletteIndberetning={kanSletteIndberetning}
                  />
                );
              }}
            </Formik>
          )}
        </div>
        <AlertRoot
          open={formModalState.warningOpen}
          setOpen={(open) => setFormModalState({ warningOpen: open })}
        >
          <Alert
            title="Er du sikker på at du vil lukke?"
            description="Der er ændringer i formularen der ikke er gemt."
            cancelButton={
              <Button color="secondary" onClick={() => {}}>
                Nej, gå tilbage til formularen
              </Button>
            }
            actionButton={
              <Button
                onClick={() => {
                  setFormModalState({
                    open: false,
                    shouldWarnBeforeClosing: false,
                    warningOpen: false,
                  });
                }}
              >
                Ja, luk
              </Button>
            }
          ></Alert>
        </AlertRoot>
      </Modal>

      <Button
        transparent
        icon
        color={"primary"}
        className={`tooltip-center `}
        aria-label={"Rediger række"}
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          setFormModalState({ open: true });
        }}
      >
        <EditIcon />
      </Button>
    </ModalRoot>
  );
}
function EfteruddannelseEditForm(props: FormProps) {
  const {
    initialValues,
    efteruddannelseIndberetningId,
    deleteIndberetninger,
    deleteIndberetningerError,
    isLoading,
    patchPeriodeError,
    formik,
    formModalState,
    setShouldWarnBeforeClosing,
    kanSletteIndberetning,
  } = props;
  const { confirmDialog } = useConfirm();
  const ingenIndberetning = !efteruddannelseIndberetningId;
  const error = patchPeriodeError ?? deleteIndberetningerError ?? undefined;
  useEffect(() => {
    if (
      formModalState &&
      formModalState.shouldWarnBeforeClosing !== formik.dirty
    ) {
      setShouldWarnBeforeClosing(formik.dirty);
    }
  }, [formik.dirty, formModalState, setShouldWarnBeforeClosing]);

  // if (formik.values.efteruddannelseIndberetningStatus === "Henlagt") {
  //   formik.values.lektionerGennemfoert = 0;
  //   formik.values.registreringsDato = "";
  // }

  return (
    <Form>
      <div className="c-item-list c-modal-container-inner">
        <div className="flex-100">
          <TextInputFormik
            disabled={isLoading}
            config={{
              component: "textinput",
              id: "bemaerkning",
              label: "Bemærkning",
              placeholder: "Bemærkning",
              initialValue: initialValues.bemaerkning,
            }}
            formik={formik}
          />
        </div>
        <div className="flex-100">
          <FormikKodeSelect
            formik={formik}
            config={{
              component: "kode-select",
              id: "efteruddannelseIndberetningStatus",
              label: "Status",
              kodeListeType: "EfteruddannelseIndberetningStatusKode",
              placeholder: "Status",
              initialValue: initialValues.efteruddannelseIndberetningStatus,
              filter:
                initialValues.efteruddannelseIndberetningStatus !==
                "FristOrdinaer"
                  ? ["FristOrdinaer"]
                  : undefined,
            }}
            onChange={(kode) => {
              const value = kode
                ? ((kode.type === "enum" ? kode.id : kode.nr.toString()) ??
                  undefined)
                : "";
              formik.setTouched({
                ...formik.touched,
                efteruddannelseIndberetningStatus: true,
              });
              formik.setFieldValue(
                "efteruddannelseIndberetningStatus",
                value,
                true,
              );
            }}
            value={formik.values[
              "efteruddannelseIndberetningStatus"
            ]?.toString()}
            disabled={ingenIndberetning || isLoading}
          />
        </div>
        {formik.values.efteruddannelseIndberetningStatus !== "Henlagt" &&
          formik.values.efteruddannelseIndberetningStatus !== "Ukendt" && (
            <div className="flex-100">
              <TextInputFormik
                disabled={
                  ingenIndberetning ||
                  isLoading ||
                  !(
                    initialValues.efteruddannelseIndberetningStatus ===
                      "FristOrdinaer" &&
                    formik.values.efteruddannelseIndberetningStatus !==
                      "FristOrdinaer"
                  ) ||
                  formik.values.efteruddannelseIndberetningStatus ===
                    "Henlagt" ||
                  !formik.values.efteruddannelseIndberetningStatus
                }
                config={{
                  component: "textinput",
                  id: "lektionerGennemfoert",
                  label: "Indberettede lektioner",
                  placeholder: "0",
                  initialValue: initialValues.lektionerGennemfoert.toString(),
                }}
                formik={formik}
              />
            </div>
          )}
        {formik.values.efteruddannelseIndberetningStatus !== "Henlagt" &&
          formik.values.efteruddannelseIndberetningStatus !== "Ukendt" && (
            <div className="flex-100">
              <FormikDatePicker
                disabled={
                  ingenIndberetning ||
                  isLoading ||
                  !(
                    initialValues.efteruddannelseIndberetningStatus ===
                      "FristOrdinaer" &&
                    formik.values.efteruddannelseIndberetningStatus !==
                      "FristOrdinaer"
                  ) ||
                  formik.values.efteruddannelseIndberetningStatus ===
                    "Henlagt" ||
                  !formik.values.efteruddannelseIndberetningStatus
                }
                config={{
                  isRequired:
                    !ingenIndberetning &&
                    Boolean(formik.errors.efteruddannelseIndberetningStatus),
                  component: "datepicker",
                  id: "registreringsDato",
                  label: "Modtaget dato",
                  initialValue: initialValues.registreringsDato,
                }}
                formik={formik}
              />
            </div>
          )}
      </div>
      {error && (
        <div className="c-modal-container-inner mb-2">
          <AlertMessage warningIcon type="danger">
            <p className="mb-0">{error.data.Message}</p>
          </AlertMessage>
        </div>
      )}
      <StickyFooter>
        <div>
          <Button
            disabled={!kanSletteIndberetning}
            type="button"
            className={isLoading ? "disabled" : ""}
            color={"secondary"}
            onClick={async () => {
              const confirmDelete = await confirmDialog(
                `Bekræft sletning af indberetning på periode`,
                <>
                  <p>
                    ”Slet indberetning” sletter advokatens allerede indsendte
                    indberetning, eller sletter muligheden for, at advokaten kan
                    foretage indberetning for perioden (hvis Status er ”Frist
                    ordinær”).
                  </p>
                  <p>
                    OBS: ”Slet indberetning” sletter også linjen for næste
                    periode inklusive evt. tekst i Bemærkningsfeltet. Afbryd og
                    kopiér teksten i Bemærkningsfeltet til den aktuelle periode
                    eller word, hvis teksten ikke må gå tabt.
                  </p>
                </>,
                "Afbryd",
                "Slet indberetning",
              );
              if (confirmDelete) {
                deleteIndberetninger();
              }
            }}
          >
            <DeleteIcon height={18} /> Slet indberetning
          </Button>
        </div>

        <div className="d-flex gap-2">
          {isLoading && <Spinner className="align-self-center" size={24} />}

          <Button
            className={
              isLoading || !formik.isValid || !formik.dirty ? "disabled" : ""
            }
            type="submit"
            color={"primary"}
            onClick={() => {}}
          >
            Gem
          </Button>
        </div>
      </StickyFooter>
    </Form>
  );
}

type FormDTO = {
  bemaerkning: string;
  efteruddannelseIndberetningStatus: string;
  lektionerGennemfoert: number;
  registreringsDato: string;
};

type FormModalProps = {
  personId: string;
  efteruddannelseId: string;
  efteruddannelseIndberetningId: string | undefined;
  invalidateData: InvalidateDataFunction<any>;
  kanSletteIndberetning: boolean;
};

type PersonEfteruddannelseDTO = {
  id: string;
  periodeFraDato: string;
  periodeTilDatoInklusiv: string;
  lektionerGennemfoert: number;
  bemaerkning: string;
  opretDato: string;
  opretInit: string;
  retteDato: string;
  retteInit: string;
  personId: string;
};

type PersonEfteruddannelseIndberetningDTO = {
  id: string;
  registreringsDato: string;
  efteruddannelseIndberetningstype: string;
  lektionerGennemfoert: number;
  efteruddannelseIndberetningStatus: string | null;
  indberetningFrist: string;
  signatur: string;
  signaturNavn: string;
  opretDato: string;
  opretInit: string;
  retteDato: string;
  retteInit: string;
  personId: string;
};
type FormProps = {
  efteruddannelseIndberetningId: string | undefined;
  initialValues: FormDTO;
  deleteIndberetninger: () => void;
  deleteIndberetningerError: PatchOperationError | null;
  isLoading: boolean;
  patchPeriodeError: PatchOperationError | null;
  formik: FormikProps<FormDTO>;
  formModalState: FormModalState;
  setShouldWarnBeforeClosing: (shouldWarnBeforeClosing: boolean) => void;
  kanSletteIndberetning: boolean;
};

type PatchOperationError = {
  status: number;
  data: {
    Message: string;
  };
};

const getValidationSchema = (initialValues, ingenIndberetning: boolean) =>
  Yup.object({
    /**
     * bemaerkning: string;
     * - Kan altid redigeres
     */
    bemaerkning: Yup.string(),
    /**
     * efteruddannelseIndberetningStatus: string;
     * - Hvis Status er "Frist ordinær" -> Status kan ændres til en anden status
     * - Hvis Status ikke er "Frist ordinær" -> Status kan ændres til en anden status DOG IKKE "Frist ordinær"
     */
    efteruddannelseIndberetningStatus: Yup.string()
      .test(
        "",
        'Status må ikke skiftes fra anden status til "Frist ordinær"',
        function (value) {
          if (ingenIndberetning) return true;
          const currentStatus = initialValues.efteruddannelseIndberetningStatus;
          if (
            currentStatus &&
            currentStatus !== "FristOrdinaer" &&
            value === "FristOrdinaer"
          ) {
            return false;
          }
          return true;
        },
      )
      .test(
        "",
        'Allerede modtaget indberetning må ikke sættes til "Frist ordinær"',
        function (value) {
          if (ingenIndberetning) return true;
          const currentStatus = initialValues.efteruddannelseIndberetningStatus;
          if (
            initialValues.registreringsDato &&
            currentStatus !== "FristOrdinaer" &&
            value === "FristOrdinaer"
          ) {
            return false;
          }
          return true;
        },
      )
      .test("", "Skal have en status", (value) => {
        if (ingenIndberetning) {
          return true;
        }

        const initialStatus = initialValues.efteruddannelseIndberetningStatus;
        if (!initialStatus) {
          return true;
        }

        return Boolean(value);
      }),
    /**
     * lektionerGennemfoert: number;
     * - Kan kun redigeres hvis status er "Frist ordinær" OG der samtidig skiftes til en anden status
     */
    lektionerGennemfoert: Yup.number()
      .typeError("Skal være tal")
      .test(
        "",
        'Kan kun redigeres, hvis status er "Frist ordinær" og ændres til en anden status',
        function (value) {
          if (ingenIndberetning) {
            return true;
          }

          if (this.parent.efteruddannelseIndberetningStatus === "Henlagt") {
            return true;
          }

          if (value === initialValues.lektionerGennemfoert) {
            return true;
          }

          const currentStatus = initialValues.efteruddannelseIndberetningStatus;
          if (
            currentStatus === "FristOrdinaer" &&
            this.parent.efteruddannelseIndberetningStatus !== "FristOrdinaer"
          ) {
            return true;
          }
          return false;
        },
      ),
    /**
     * registreringsDato: string;
     * - Kan kun redigeres hvis status er "Frist ordinær" OG der samtidig skiftes til en anden status
     */
    registreringsDato: Yup.date()
      .nullable()
      .test(
        "",
        'Kan kun redigeres, hvis status er "Frist ordinær" og ændres til en anden status',
        function (value) {
          if (ingenIndberetning) {
            return true;
          }

          if (this.parent.efteruddannelseIndberetningStatus === "Henlagt") {
            return true;
          }

          if (!value && !initialValues.registreringsDato) {
            return true;
          }

          if (
            value &&
            differenceInDays(
              new Date(value),
              new Date(initialValues.registreringsDato),
            ) === 0
          ) {
            return true;
          }

          const currentStatus = initialValues.efteruddannelseIndberetningStatus;
          if (
            currentStatus === "FristOrdinaer" &&
            this.parent.efteruddannelseIndberetningStatus !== "FristOrdinaer"
          ) {
            return true;
          }
          return false;
        },
      )
      .test("", "Påkrævet", function (value) {
        if (value) return true;
        if (ingenIndberetning) return true;
        const previousStatus = initialValues.efteruddannelseIndberetningStatus;
        const newStatus = this.parent.efteruddannelseIndberetningStatus;
        if (newStatus === "Henlagt") return true;
        if (
          previousStatus === "FristOrdinaer" &&
          newStatus === "FristOrdinaer" &&
          !value
        ) {
          return true;
        }

        if (this.parent.efteruddannelseIndberetningStatus === "Henlagt") {
          return true;
        }

        return (
          Boolean(value) === true &&
          newStatus &&
          initialValues.efteruddannelseIndberetningStatus
        );
      }),
  });
